// components/Header.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { trackActivity } from "../utils/activityTracker";
import { getAlerts } from "../controllers/alertController";
import { getVisibleTabs } from "../controllers/tabController";
import AlertBox from "./AlertBox";
import SuperAdminClientSwitcher from "./SuperAdminClientSwitcher";
import { logoutUser } from "../controllers/authController";
import { useBranch } from "../context/BranchContext";
import { getApiBase } from "../utils/api";
import FieldHelp from "../help/FieldHelp";

// Last known tab list per user. Seeding state from this is what stops a hidden
// tab flashing into view for one frame while GET /api/tabs is still in flight.
// Bumped from "enviz-visible-tabs" when the payload gained per-tab states, so a
// stale array-shaped entry from the previous release is simply ignored.
const TAB_CACHE_PREFIX = "enviz-tab-states:";

const normaliseTabEntry = (value) =>
  typeof value === "string"
    ? { state: value, badge: null }
    : { state: value && value.state === "disabled" ? "disabled" : "shown",
        badge: (value && value.badge) || null };

const readCachedTabStates = (username) => {
  if (!username) return null;
  try {
    const raw = localStorage.getItem(TAB_CACHE_PREFIX + username);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached || typeof cached !== "object" || Array.isArray(cached)) return null;
    const labels = Object.keys(cached);
    if (labels.length === 0) return null;

    const states = {};
    labels.forEach((label) => {
      states[label] = normaliseTabEntry(cached[label]);
    });
    return states;
  } catch (e) {
    return null;
  }
};

const writeCachedTabStates = (username, states) => {
  if (!username) return;
  try {
    if (states) {
      localStorage.setItem(TAB_CACHE_PREFIX + username, JSON.stringify(states));
    } else {
      localStorage.removeItem(TAB_CACHE_PREFIX + username);
    }
  } catch (e) {
    // Storage full or blocked; the nav still works, it just flashes once.
  }
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBranch, setSelectedBranch, branchList, refreshBranches } = useBranch();
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  let user = null;
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      user = JSON.parse(storedUser);
    }
  } catch (error) {
    user = null;
  }

  const storedUsername =
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    user?.emailAddress;

  const [unreadCount, setUnreadCount] = useState(0);
  // label -> "shown" | "disabled", from GET /api/tabs. Tabs the user may not see
  // at all are absent. null means "no usable list" — either still loading (see
  // tabsResolved) or the request failed, in which case we fail open rather than
  // render an empty navigation.
  const [tabStates, setTabStates] = useState(() =>
    readCachedTabStates(storedUsername),
  );
  // False until the request settles. While false with no cached list we hide the
  // gated items, because failing open that early is exactly what made a
  // switched-off tab appear for a split second on reload.
  const [tabsResolved, setTabsResolved] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [energyDropdownOpen, setEnergyDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  // Active Live sub-tab, kept in sync with LiveTab (sessionStorage + custom event).
  const [activeLiveTab, setActiveLiveTab] = useState(
    () => sessionStorage.getItem("enviz-pending-tab") || "diagram",
  );

  const closeAllDropdowns = () => {
    setEnergyDropdownOpen(false);
    setProfileDropdownOpen(false);
    setBranchDropdownOpen(false);
  };

  // LiveTab can switch its own tab; mirror it so the tab bar highlight stays correct.
  useEffect(() => {
    const onTabChange = (e) => {
      if (e?.detail) setActiveLiveTab(e.detail);
    };
    window.addEventListener("enviz-tab-change", onTabChange);
    return () => window.removeEventListener("enviz-tab-change", onTabChange);
  }, []);

  useEffect(() => {
    if (storedUsername && refreshBranches) {
      refreshBranches();
    }
  }, [storedUsername, refreshBranches]);

  // Tab visibility is configured per user in the Step 2 admin portal.
  useEffect(() => {
    let cancelled = false;
    if (!storedUsername) {
      // Nothing to scope by; fail open instead of hiding the whole nav.
      setTabStates(null);
      setTabsResolved(true);
      return undefined;
    }

    // A different user may have signed in without this component remounting;
    // re-seed from their cache so we never reuse the previous user's list.
    setTabStates(readCachedTabStates(storedUsername));
    setTabsResolved(false);

    const loadTabs = async () => {
      try {
        const tabs = await getVisibleTabs(storedUsername);
        if (cancelled) return;

        // Older backends answer /tabs with a flat list whose labels predate this
        // navigation ("Info", "Customization", ...). Trusting that would hide
        // almost every nav item, so only honour the response once it carries the
        // per-tab ids the current endpoint returns. Anything else fails open.
        const isCurrentShape =
          tabs.length > 0 && tabs.every((tab) => typeof tab.id === "number");

        if (!isCurrentShape) {
          if (tabs.length > 0) {
            console.warn(
              "[Header] /api/tabs returned a legacy payload; showing all tabs.",
              "Update the backend to apply per-user tab access.",
            );
          }
          setTabStates(null);
          writeCachedTabStates(storedUsername, null);
          return;
        }

        const states = {};
        tabs.forEach((tab) => {
          states[tab.label] = {
            state: tab.state === "disabled" ? "disabled" : "shown",
            badge: tab.badge || null,
          };
        });
        setTabStates(states);
        writeCachedTabStates(storedUsername, states);
      } catch (e) {
        // Backend unreachable: fail open rather than strand the user with no nav.
        if (!cancelled) setTabStates(null);
      } finally {
        if (!cancelled) setTabsResolved(true);
      }
    };

    loadTabs();
    return () => {
      cancelled = true;
    };
  }, [storedUsername]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!storedUsername) return;
      try {
        const data = await getAlerts(storedUsername);
        setUnreadCount(data.unread || 0);
      } catch (e) { }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [storedUsername]);

  const apiBase = getApiBase() || "/api";
  const remoteLogo = storedUsername && !logoError ? `${apiBase}/get_logo?username=${storedUsername}` : null;
  const companyLogo = remoteLogo || user?.logo || user?.logoUrl || user?.logoURL || user?.companyLogo || user?.clientLogo || user?.brandingLogo;
  const profileImage = user?.profilePicture || user?.profile_image || user?.profileImage || user?.profile_pic || user?.avatar || user?.avatarUrl || user?.avatarURL || user?.photo || user?.photoUrl || user?.photoURL || user?.image || user?.imageUrl || user?.imageURL;
  const avatarImage = companyLogo || profileImage;
  const userInitial = user?.displayName?.charAt(0)?.toUpperCase();

  const handleLogout = () => {
    trackActivity({ action: "LOGOUT", tab_name: "Header" });
    // Clears the stored username and any super admin identity too, so the next
    // sign-in never inherits the previous session's client.
    logoutUser();
    window.location.href = "/login";
  };

  // Nav items are matched by label against what GET /api/tabs returned.
  const isTabVisible = (label) => {
    // Still waiting on the first response and nothing cached to go on: withhold
    // the gated items for that one frame instead of showing tabs that may be off.
    if (!tabsResolved && !tabStates) return false;
    // No usable list (request failed or legacy backend): show everything.
    if (!tabStates) return true;
    return Object.prototype.hasOwnProperty.call(tabStates, label);
  };

  // Greyed out: the tab stays in the menu but cannot be opened.
  const isTabDisabled = (label) =>
    Boolean(tabStates) && tabStates[label] && tabStates[label].state === "disabled";

  // Short tag rendered on the entry, e.g. "SOON" on a placeholder tab.
  const tabBadge = (label) =>
    (tabStates && tabStates[label] && tabStates[label].badge) || null;

  const GREYED_TITLE = "Not enabled for your account";

  // Matches the disabled "Flow" placeholder already in this header.
  const greyedOut = { opacity: 0.55, cursor: "not-allowed", userSelect: "none" };

  /**
   * Top-level nav entry (Efficiency, Temperature, Flow). Returns null when
   * hidden. `to` may be null for a tab that has no page yet, in which case it
   * always renders as a non-clickable placeholder.
   */
  const renderTopTab = (label, to, active) => {
    if (!isTabVisible(label)) return null;
    const badge = tabBadge(label);
    const greyed = isTabDisabled(label) || !to;
    const lit = active && !greyed;
    const style = {
      ...styles.link,
      backgroundColor: lit ? "#fff" : "transparent",
      color: lit ? "#1BA08E" : "#fff",
      fontWeight: lit ? 700 : 600,
      boxShadow: lit ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
      border: lit ? "1.5px solid #1BA08E" : "1.5px solid transparent",
      transition: "all 0.2s",
      ...(greyed ? greyedOut : {}),
      ...(badge ? { position: "relative" } : {}),
    };

    const badgeEl = badge ? (
      <span
        style={{
          position: "absolute", top: -6, right: -4, background: "#f59e0b",
          color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 4px",
          borderRadius: 4, letterSpacing: "0.03em",
        }}
      >
        {/* {badge} */}
      </span>
    ) : null;

    if (greyed) {
      return (
        <div style={style} title={GREYED_TITLE}>
          {label}
          {badgeEl}
        </div>
      );
    }

    return (
      <NavLink to={to} onClick={closeAllDropdowns} style={() => style}>
        {label}
        {badgeEl}
      </NavLink>
    );
  };

  /** Entry inside the Energy dropdown. Returns null when hidden. */
  const renderMenuItem = (label, onClick, active, showDivider) => {
    if (!isTabVisible(label)) return null;
    const greyed = isTabDisabled(label);
    const restColor = greyed ? "#94a3b8" : active ? "#1BA08E" : "#374151";

    return (
      <div
        onClick={greyed ? undefined : onClick}
        title={greyed ? GREYED_TITLE : undefined}
        style={{
          padding: "10px 16px",
          fontSize: 13,
          fontWeight: 500,
          color: restColor,
          cursor: greyed ? "not-allowed" : "pointer",
          borderBottom: showDivider ? "1px solid #f1f5f9" : "none",
          ...(greyed ? { userSelect: "none" } : {}),
        }}
        onMouseEnter={(e) => {
          if (greyed) return;
          e.currentTarget.style.background = "#f0fdf4";
          e.currentTarget.style.color = "#1BA08E";
        }}
        onMouseLeave={(e) => {
          if (greyed) return;
          e.currentTarget.style.background = "#fff";
          e.currentTarget.style.color = restColor;
        }}
      >
        {label}
      </div>
    );
  };

  // Bill Reconciliation used to be hardcoded to the 'jawa' username. It is now
  // driven by app_tabs.restricted_to_username, so Step 2 controls who gets it.
  const showBillReconciliation = isTabVisible("Bill Reconciliation");

  const isEfficiencyActive = location.pathname.startsWith("/tools/efficiency-initiatives");
  const isEnergyActive =
    location.pathname.startsWith("/live") ||
    location.pathname.startsWith("/historical") ||
    location.pathname.startsWith("/ensight") ||
    location.pathname.startsWith("/tools/off-grid-calculator") ||
    location.pathname.startsWith("/bill-reconciliation") ||
    location.pathname.startsWith("/customization");
  const isTemperatureActive = location.pathname.startsWith("/tools/environment");

  const liveSubItems = [
    { key: "diagram", label: "Diagram View" },
    { key: "table", label: "Table View" },
    { key: "distribution", label: "Distribution View" },
    { key: "forecast", label: "Forecasting" },
    { key: "customization", label: "Customizations", path: "/customization" },
  ].filter((item) => isTabVisible(item.label));

  const historicalSubItems = [
    { path: "/historical", label: "Overview" },
    { path: "/ensight", label: "Insights" },
  ].filter((item) => isTabVisible(item.label));

  // NOTE: deliberately not gated by label. These are Temperature's own views and
  // their labels ("Live", "Historical") collide with the Energy entries of the
  // same name, so matching them against the catalogue would apply the wrong
  // tab's state. Reaching them already requires Temperature itself to be open.
  const temperatureSubItems = [
    { path: "/tools/environment", label: "Live" },
    { path: "/tools/environment/historical", label: "Historical" },
    { path: "/tools/environment/efficiency", label: "Efficiency" },
  ];

  const goToLiveTab = (item) => {
    // Every Live sub-view can be switched off for a user, in which case callers
    // reaching for liveSubItems[0] hand us nothing.
    if (!item) {
      navigate("/live");
      closeAllDropdowns();
      return;
    }
    if (item.path) {
      navigate(item.path);
    } else {
      sessionStorage.setItem("enviz-pending-tab", item.key);
      window.dispatchEvent(new CustomEvent("enviz-tab-change", { detail: item.key }));
      setActiveLiveTab(item.key);
      navigate("/live");
    }
    closeAllDropdowns();
  };

  // Depend on a primitive: liveSubItems is rebuilt every render, so using the
  // array itself would re-run the effect below on every single render. Only the
  // clickable sub-views count — landing LiveTab on a greyed-out one would show
  // content the user is not supposed to open.
  const openableLiveKeys = liveSubItems
    .filter((item) => !isTabDisabled(item.label))
    .map((item) => item.key)
    .join(",");

  // The remembered sub-tab may since have been switched off or greyed out for
  // this user; fall back to the first one they can still open.
  useEffect(() => {
    if (!tabStates || !openableLiveKeys) return;

    const keys = openableLiveKeys.split(",");
    if (keys.includes(activeLiveTab)) return;

    const fallback = keys[0];
    sessionStorage.setItem("enviz-pending-tab", fallback);
    window.dispatchEvent(new CustomEvent("enviz-tab-change", { detail: fallback }));
    setActiveLiveTab(fallback);
  }, [tabStates, openableLiveKeys, activeLiveTab]);

  // Sub-navigation shown as a tab bar under the header, contextual to the section.
  // Live views now use the floating View card directly inside the dashboard area.
  const isCustomizationSection = location.pathname.startsWith("/customization");
  const isHistoricalSection =
    location.pathname.startsWith("/historical") || location.pathname.startsWith("/ensight");
  const isTemperatureSection = location.pathname.startsWith("/tools/environment");

  const subTabs = isCustomizationSection
    ? [
        {
          key: "customization",
          label: "Customizations",
          disabled: isTabDisabled("Customizations"),
          active: true,
          onClick: () => navigate("/customization"),
        },
      ]
    : isHistoricalSection
      ? historicalSubItems.map((item) => ({
          key: item.path,
          label: item.label,
          disabled: isTabDisabled(item.label),
          active:
            item.path === "/historical"
              ? location.pathname === "/historical"
              : location.pathname.startsWith(item.path),
          onClick: () => {
            navigate(item.path);
            closeAllDropdowns();
          },
        }))
      : isTemperatureSection
        ? temperatureSubItems.map((item) => ({
            key: item.path,
            label: item.label,
            disabled: false,
            // "/tools/environment" is Live, so match it exactly (Historical is a sub-path).
            active:
              item.path === "/tools/environment"
                ? location.pathname === "/tools/environment"
                : location.pathname.startsWith(item.path),
            onClick: () => {
              navigate(item.path);
              closeAllDropdowns();
            },
          }))
        : [];

  return (
    <>
    <div style={styles.header}>
      <div style={styles.brandArea}>
        <a href="https://enfo.ai/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          <div style={styles.logoWrapper}>
            <h2 style={styles.logo}>
              <span style={styles.logoPrimary}>En</span>
              <span style={styles.logoSecondary}>Viz</span>
            </h2>
          </div>
        </a>
        <nav style={styles.nav}>

          {/* Efficiency Tab */}
          {renderTopTab(
            "Efficiency",
            "/tools/efficiency-initiatives",
            isEfficiencyActive,
          )}
          {/* Energy Tab with hover dropdown */}
          {isTabVisible("Energy") && (
            <div
              style={{ position: "relative" }}
              onMouseEnter={() => {
                if (!isTabDisabled("Energy")) setEnergyDropdownOpen(true);
              }}
              onMouseLeave={() => setEnergyDropdownOpen(false)}
            >
              <div
                title={isTabDisabled("Energy") ? GREYED_TITLE : undefined}
                style={{
                ...styles.link,
                backgroundColor: isEnergyActive && !isTabDisabled("Energy") ? "#fff" : "transparent",
                color: isEnergyActive && !isTabDisabled("Energy") ? "#1BA08E" : "#fff",
                fontWeight: isEnergyActive && !isTabDisabled("Energy") ? 700 : 600,
                boxShadow: isEnergyActive && !isTabDisabled("Energy") ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
                border: isEnergyActive && !isTabDisabled("Energy") ? "1.5px solid #1BA08E" : "1.5px solid transparent",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
                ...(isTabDisabled("Energy") ? greyedOut : {}),
              }}>
                Energy <span style={{ fontSize: 8, opacity: 0.6 }}>&#9660;</span>
              </div>
              {energyDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, marginTop: 0,
                  background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 180, overflow: "visible",
                }}>
                  {/* Live -> opens Diagram View by default; sub-views live in the tab bar below */}
                  {renderMenuItem(
                    "Live",
                    () => goToLiveTab(liveSubItems.find((i) => !isTabDisabled(i.label))),
                    location.pathname.startsWith("/live"),
                    true,
                  )}

                  {/* Historical -> opens Overview by default; sub-views in the tab bar below */}
                  {renderMenuItem(
                    "Historical",
                    () => { navigate("/historical"); closeAllDropdowns(); },
                    location.pathname.startsWith("/historical") ||
                      location.pathname.startsWith("/ensight"),
                    true,
                  )}

                  {/* Off Grid Calculator */}
                  {renderMenuItem(
                    "Off Grid Calculator",
                    () => { navigate("/tools/off-grid-calculator"); closeAllDropdowns(); },
                    location.pathname.startsWith("/tools/off-grid-calculator"),
                    showBillReconciliation,
                  )}

                  {/* Bill Reconciliation (restricted; see Step 2 tab access) */}
                  {renderMenuItem(
                    "Bill Reconciliation",
                    () => { navigate("/bill-reconciliation"); closeAllDropdowns(); },
                    location.pathname.startsWith("/bill-reconciliation"),
                    false,
                  )}
                </div>
              )}
            </div>
          )}
          {/* Temperature Tab */}
          {renderTopTab("Temperature", "/tools/environment", isTemperatureActive)}
          {/* Flow — no route yet; driven by app_tabs like every other tab. */}
          {renderTopTab("Flow", null, false)}

          {/* Branches dropdown */}
          {branchList.length > 0 && (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => { setEnergyDropdownOpen(false); setProfileDropdownOpen(false); setBranchDropdownOpen((v) => !v); }}
                style={{ ...styles.link, cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", display: "flex", alignItems: "center", gap: 4, outline: "none", background: "transparent", border: "1.5px solid transparent", transition: "all 0.2s" }}
              >
                <FieldHelp id="common.branchSelector">Branches</FieldHelp> <span style={{ fontSize: 8, opacity: 0.6 }}>&#9660;</span>
              </button>
              {branchDropdownOpen && (
                <>
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={closeAllDropdowns}></div>
                  <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 180, overflow: "hidden" }}>
                    <div
                      onClick={() => { setSelectedBranch("All"); closeAllDropdowns(); }}
                      style={{ padding: "10px 16px", fontSize: 13, fontWeight: selectedBranch === "All" ? 700 : 500, color: selectedBranch === "All" ? "#1BA08E" : "#374151", cursor: "pointer", borderBottom: branchList.length > 0 ? "1px solid #f1f5f9" : "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                      onMouseEnter={(e) => { e.target.style.background = "#f0fdf4"; e.target.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.color = selectedBranch === "All" ? "#1BA08E" : "#374151"; }}
                    >
                      All Branches {selectedBranch === "All" && <span>&#10003;</span>}
                    </div>
                    {branchList.map((branch, idx) => (
                      <div key={branch.name}
                        onClick={() => { setSelectedBranch(branch.name); closeAllDropdowns(); }}
                        style={{ padding: "10px 16px", fontSize: 13, fontWeight: selectedBranch === branch.name ? 700 : 500, color: selectedBranch === branch.name ? "#1BA08E" : "#374151", cursor: "pointer", borderBottom: idx !== branchList.length - 1 ? "1px solid #f1f5f9" : "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        onMouseEnter={(e) => { e.target.style.background = "#f0fdf4"; e.target.style.color = "#1BA08E"; }}
                        onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.color = selectedBranch === branch.name ? "#1BA08E" : "#374151"; }}
                      >
                        {branch.name} {selectedBranch === branch.name && <span>&#10003;</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </nav>
      </div>

      <div style={styles.right}>
        {user && (
          <>
            {/* Super admin only; renders nothing for a client session. */}
            <SuperAdminClientSwitcher />

            <div style={{ position: "relative", marginRight: "4px" }}>
              <button onClick={() => setShowAlerts(!showAlerts)} style={styles.bellButton} title="Notifications">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                {unreadCount > 0 && (
                  <span style={styles.notificationBadge}><FieldHelp id="common.unreadAlerts">{unreadCount > 9 ? "9+" : unreadCount}</FieldHelp></span>
                )}
              </button>
              <AlertBox
                isOpen={showAlerts}
                onClose={() => {
                  setShowAlerts(false);
                  if (storedUsername) {
                    getAlerts(storedUsername).then(data => setUnreadCount(data.unread || 0)).catch(() => { });
                  }
                }}
              />
            </div>

            {/* Client Name / Profile Dropdown */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => { setEnergyDropdownOpen(false); setBranchDropdownOpen(false); setProfileDropdownOpen((v) => !v); }}
                style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 8, transition: "background 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span style={styles.clientName}><FieldHelp id="common.clientName">{user?.client_name || user?.username}</FieldHelp></span>
                {avatarImage ? (
                  <img src={avatarImage} alt={user?.displayName || "Profile"} style={styles.avatar} onError={() => setLogoError(true)} />
                ) : (
                  userInitial && <div style={styles.avatarFallback}>{userInitial}</div>
                )}
                <span style={{ fontSize: 8, opacity: 0.6, color: "#fff" }}>&#9660;</span>
              </button>

              {profileDropdownOpen && (
                <>
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={() => setProfileDropdownOpen(false)}></div>
                  <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.14)", zIndex: 100, minWidth: 190, overflow: "hidden" }}>
                    <div
                      onClick={() => { navigate("/info"); setProfileDropdownOpen(false); }}
                      style={{ padding: "11px 16px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#374151"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      Settings
                    </div>
                    <div
                      onClick={() => { navigate("/tools/complaint-ticket"); setProfileDropdownOpen(false); }}
                      style={{ padding: "11px 16px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#374151"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      Complaint Ticket
                    </div>
                    <div
                      onClick={() => { setProfileDropdownOpen(false); handleLogout(); }}
                      style={{ padding: "11px 16px", fontSize: 13, fontWeight: 500, color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Logout
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>

    {/* Contextual sub-navigation tabs (Live views / Historical views) */}
    {subTabs.length > 0 && (
      <div style={styles.subTabBar}>
        {subTabs.map((tab) => (
          <div
            key={tab.key}
            onClick={tab.disabled ? undefined : tab.onClick}
            title={tab.disabled ? GREYED_TITLE : undefined}
            style={{
              ...styles.subTab,
              ...(tab.active && !tab.disabled ? styles.subTabActive : {}),
              ...(tab.disabled ? greyedOut : {}),
            }}
            onMouseEnter={(e) => {
              if (!tab.active && !tab.disabled) e.currentTarget.style.color = "#1BA08E";
            }}
            onMouseLeave={(e) => {
              if (!tab.active && !tab.disabled) e.currentTarget.style.color = "#64748b";
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>
    )}
    </>
  );
};

const styles = {
  subTabBar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "0 24px",
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  subTab: {
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "color 0.15s, border-color 0.15s",
    whiteSpace: "nowrap",
  },
  subTabActive: {
    color: "#1BA08E",
    borderBottom: "2px solid #1BA08E",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 20px",
    backgroundColor: "#1BA08E",
    color: "white",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    gap: "16px",
    flexWrap: "wrap",
    minHeight: "50px",
    boxSizing: "border-box",
  },
  brandArea: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
    padding: "4px 12px 4px 10px",
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(27,160,142,0.08)",
  },
  logo: {
    margin: 0,
    fontSize: "20px",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "baseline",
    gap: "2px",
    fontWeight: 700,
  },
  logoPrimary: { color: "#1BA08E" },
  logoSecondary: { color: "#3D5DAA" },
  nav: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  link: {
    padding: "5px 12px",
    borderRadius: "6px",
    color: "#fff",
    transition: "all 0.2s",
    fontWeight: 600,
    fontSize: "13.5px",
    textDecoration: "none",
    display: "inline-block",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  avatar: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    objectFit: "contain",
    backgroundColor: "#fff",
    padding: "2px",
  },
  avatarFallback: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    backgroundColor: "#1BA08E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: "13px",
    color: "#fff",
  },
  clientName: {
    color: "#fff",
    fontWeight: "600",
    fontSize: "14px",
  },
  bellButton: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#fff",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    transition: "background-color 0.2s",
    position: "relative",
  },
  notificationBadge: {
    position: "absolute",
    top: "2px",
    right: "2px",
    minWidth: "16px",
    height: "16px",
    backgroundColor: "#ff4d4f",
    borderRadius: "8px",
    border: "1.5px solid #1BA08E",
    color: "#fff",
    fontSize: "10px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 3px",
  },
};

export default Header;
